import Head from "next/head";
import { Locale, useRouter } from "next/router";

import localizeHref from "../../../lib/multilingual/localizeHref";
import { CANONICAL_HOST } from "../../config";
import { cleanSearchParams } from "@/common/utils/router.utils";
import { withLocale } from "@/common/utils/url.utils";
import { withUsHreflang } from "@/common/utils/us-launch.utils";
import { US_LOCALE } from "@/i18n/config";
import { removeTrailingSlash } from "@/utils/removeTrailingSlash";
import removeLocaleFromPath from "@whoppah/utils/removeLocaleFromPath";

const WHITELIST = ["page"];

// LinkHTMLAttributes contains `hrefLang` but in react server render
// camelCase properties remain camelCase. We don't want that for SEO
// purposes. It sucks.
// https://github.com/facebook/react/issues/13162
declare module "react" {
  interface LinkHTMLAttributes<T> extends HTMLAttributes<T> {
    hreflang?: string | undefined;
  }
}

const containsDynamicParam = (url: string): boolean => {
  return url.includes("[") || url.includes("]");
};

const HrefLang = ({ urlMap }: { urlMap?: Partial<Record<string, string>> }) => {
  const router = useRouter();
  const { locales, defaultLocale, route } = router;

  // We don't need hreflang for 404 pages
  if (route === "/404") {
    return null;
  }

  const asPath = removeTrailingSlash(router.asPath);

  // If asPath contains dynamic param (which shouldn't happen)
  // we return null because there is no point of adding it to hreflang.
  if (containsDynamicParam(asPath)) {
    return null;
  }

  // A urlMap is the explicit cluster. Locales with no key are omitted so
  // a fallback render (WHO-5504) is not advertised as an alternate. `/en-us`
  // is left to `withUsHreflang`, which derives it from the `en` URL
  // (WHO-5766), so it is listed once and only while the flag is on.
  const routedLocales = locales.filter(locale => locale !== US_LOCALE);
  const clusterLocales = urlMap
    ? routedLocales.filter(locale => Boolean(urlMap[locale]))
    : routedLocales;
  const xDefaultLocale = urlMap
    ? urlMap[defaultLocale]
      ? defaultLocale
      : clusterLocales[0]
    : defaultLocale;

  if (!xDefaultLocale || clusterLocales.length === 0) {
    return null;
  }

  const addLocalePrefix = (path: string, locale: Locale) =>
    withLocale(path, locale, defaultLocale);

  const getUrlPath = (locale: Locale) => {
    // If urlMap is passed we use it
    if (urlMap && urlMap[locale]) {
      return urlMap[locale];
    }

    // If urlMap is not passed we use asPath
    const urlPath = router.asPath;
    // Remove locale from path
    const urlPathWithoutLocale = removeLocaleFromPath(urlPath);
    // Localize path
    return localizeHref(urlPathWithoutLocale, router.locale, locale);
  };

  const getUrl = (locale: Locale) => {
    // Get url path from urlMap or asPath
    const urlPath = getUrlPath(locale);

    // Add locale prefix to path
    const path = addLocalePrefix(urlPath, locale);

    // Create url object with canonical domain
    const url = new URL(path, CANONICAL_HOST);

    // A urlMap entry is a bare path, so page 2+ of a listing would point every
    // alternate at page 1 while the canonical keeps the page (WHO-5477 audit,
    // 2026-09-03). Carry the whitelisted params of the current URL over.
    const current = new URL(router.asPath, CANONICAL_HOST);
    for (const key of WHITELIST) {
      const value = current.searchParams.get(key);

      if (value !== null && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    }

    // Clean search params
    cleanSearchParams(url.searchParams);

    // We remove all the search params that are not in whitelist. A copy of the
    // keys: deleting from the live iterator skips the key after each one deleted,
    // which let `?currency=` into the alternates (WHO-6199).
    for (const key of [...url.searchParams.keys()]) {
      if (!WHITELIST.includes(key)) {
        url.searchParams.delete(key);
      }
    }

    // Return complete url
    return removeTrailingSlash(url.href);
  };

  return (
    <Head>
      <link
        key="default-hreflang"
        rel="alternate"
        hreflang="x-default"
        href={getUrl(xDefaultLocale)}
      />
      {withUsHreflang(
        clusterLocales.map(locale => ({ locale, href: getUrl(locale) }))
      ).map(({ locale, href }) => (
        <link
          key={`${locale}-hreflang`}
          rel="alternate"
          hreflang={locale}
          href={href}
        />
      ))}
    </Head>
  );
};

export default HrefLang;
